/**
 * Structured Data Utilities
 * 
 * Generates JSON-LD structured data for SEO
 */

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "OceanNova",
  "alternateName": ["Ocean Nova", "Ocean Nova Shipping", "OceanNova Shipping"],
  "description": "Professional freight forwarding and shipping agency services with 25+ years of experience in maritime logistics, port operations, and multimodal transport.",
  "url": "https://oceannova.pl",
  "logo": {
    "@type": "ImageObject",
    "url": "https://oceannova.pl/assets/oceannova-icon.png",
    "width": 512,
    "height": 512,
    "caption": "OceanNova Logo"
  },
  "image": "https://oceannova.pl/assets/oceannova-icon.png",
  "foundingDate": "2025",
  "areaServed": {
    "@type": "GeoCircle",
    "geoMidpoint": {
      "@type": "GeoCoordinates",
      "latitude": "54.5189",
      "longitude": "18.5305"
    },
    "geoRadius": "Global"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "Customer Service",
    "availableLanguage": ["English", "Polish"],
    "areaServed": "Worldwide"
  },
  "sameAs": [],
};

export const serviceSchema = (serviceName: string, description: string, url: string) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": serviceName,
  "description": description,
  "provider": {
    "@type": "Organization",
    "name": "OceanNova",
    "url": "https://oceannova.pl"
  },
  "url": url,
  "areaServed": {
    "@type": "Place",
    "name": "Worldwide"
  },
  "serviceType": "Freight Forwarding and Logistics"
});

export const breadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": `https://oceannova.pl${item.url}`
  }))
});

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "OceanNova",
  "url": "https://oceannova.pl",
  "description": "Professional freight forwarding and shipping agency services with 25+ years of maritime logistics expertise.",
  "potentialAction": {
    "@type": "ContactAction",
    "target": "https://oceannova.pl/contact",
    "name": "Contact OceanNova"
  }
};

export const siteNavigationSchema = {
  "@context": "https://schema.org",
  "@type": "SiteNavigationElement",
  "name": "Main Navigation",
  "hasPart": [
    {
      "@type": "SiteNavigationElement",
      "name": "Home",
      "url": "https://oceannova.pl/"
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Services",
      "url": "https://oceannova.pl/services/port-ship-agency",
      "hasPart": [
        {
          "@type": "SiteNavigationElement",
          "name": "Port & Ship Agency",
          "url": "https://oceannova.pl/services/port-ship-agency"
        },
        {
          "@type": "SiteNavigationElement",
          "name": "Ship Chartering",
          "url": "https://oceannova.pl/services/ship-chartering"
        },
        {
          "@type": "SiteNavigationElement",
          "name": "Terminal & Port Development",
          "url": "https://oceannova.pl/services/terminal-port-development"
        },
        {
          "@type": "SiteNavigationElement",
          "name": "Air Freight",
          "url": "https://oceannova.pl/services/air-freight"
        },
        {
          "@type": "SiteNavigationElement",
          "name": "Rail Logistics",
          "url": "https://oceannova.pl/services/rail-logistics"
        },
        {
          "@type": "SiteNavigationElement",
          "name": "Road Transport",
          "url": "https://oceannova.pl/services/road-transport"
        },
        {
          "@type": "SiteNavigationElement",
          "name": "Project Cargo",
          "url": "https://oceannova.pl/services/project-cargo"
        }
      ]
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Our Story",
      "url": "https://oceannova.pl/our-story"
    },
    {
      "@type": "SiteNavigationElement",
      "name": "Contact",
      "url": "https://oceannova.pl/contact"
    }
  ]
};

export const servicesListSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "OceanNova Services",
  "description": "Comprehensive freight forwarding and maritime logistics services",
  "numberOfItems": 7,
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Port & Ship Agency",
      "url": "https://oceannova.pl/services/port-ship-agency"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Ship Chartering",
      "url": "https://oceannova.pl/services/ship-chartering"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Terminal & Port Development",
      "url": "https://oceannova.pl/services/terminal-port-development"
    },
    {
      "@type": "ListItem",
      "position": 4,
      "name": "Air Freight",
      "url": "https://oceannova.pl/services/air-freight"
    },
    {
      "@type": "ListItem",
      "position": 5,
      "name": "Rail Logistics",
      "url": "https://oceannova.pl/services/rail-logistics"
    },
    {
      "@type": "ListItem",
      "position": 6,
      "name": "Road Transport",
      "url": "https://oceannova.pl/services/road-transport"
    },
    {
      "@type": "ListItem",
      "position": 7,
      "name": "Project Cargo",
      "url": "https://oceannova.pl/services/project-cargo"
    }
  ]
};

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "OceanNova",
  "image": {
    "@type": "ImageObject",
    "url": "https://oceannova.pl/assets/oceannova-icon.png",
    "width": 512,
    "height": 512
  },
  "logo": {
    "@type": "ImageObject",
    "url": "https://oceannova.pl/assets/oceannova-icon.png",
    "width": 512,
    "height": 512
  },
  "@id": "https://oceannova.pl",
  "url": "https://oceannova.pl",
  "priceRange": "$$",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "PL"
  },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday"
    ],
    "opens": "08:00",
    "closes": "17:00"
  }
};

export const faqSchema = (questions: Array<{ question: string; answer: string }>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": questions.map(q => ({
    "@type": "Question",
    "name": q.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": q.answer
    }
  }))
});
